import React, { useState, useMemo, useCallback } from 'react';
import {
  committeeTerms as termsApi,
  committeeMembers as membersApi,
  secretaryHandovers as handoversApi,
  batch
} from '../../api/endpoints';
import type { BatchOperation } from '../../api/endpoints';
import {
  useMultiLedger, useSearch, usePagination, useNotification, useOptionList, useDateRangeFilter,
  useOpenRecordFromLink, findLedgerRow, RECORD_GONE
} from '../../hooks';
import { formatDate } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import { missingRequired, requiredError } from '../../utils/requiredFields';
import {
  PageHeader, SubTabs, SearchBar, DateRangeFilter, Pagination, Notification, PromptDialog, FormLayout, Input, Select, SearchableSelect, Badge, Button, TablePanel, Modal
} from '../../components/ui';
import PrintButton from '../../components/ui/PrintButton';
import CommitteeSummary from './CommitteeSummary';

const TERM_STATUSES = [
  { value: 'Active', label: 'Active / Current Term' },
  { value: 'Completed', label: 'Completed / Historical' }
];
const MEMBER_STATUSES = [
  { value: 'Active', label: 'Active (Serving)' },
  { value: 'Completed', label: 'Completed Term' },
  { value: 'Resigned', label: 'Resigned' },
  { value: 'Deceased', label: 'Deceased' }
];
const HANDOVER_STATUSES = [
  { value: 'Pending', label: 'Pending Validation' },
  { value: 'Completed', label: 'Completed & Signed' }
];

const CHECKLIST = [
  { key: 'adminKeys', label: 'Office Keys & Safe Access' },
  { key: 'minuteBooks', label: 'Committee Minute Books' },
  { key: 'finRecords', label: 'Financial Records & Cheque Books' },
  { key: 'legalDocs', label: 'Property & Legal Documents' }
];

const EMPTY_TERM = { name: '', startDate: '', endDate: '', status: 'Active', remarks: '' };
const EMPTY_MEMBER = { termRef: '', name: '', role: '', fromDate: '', toDate: '', status: 'Active', remarks: '' };
const EMPTY_HANDOVER = {
  date: '', outgoing: '', incoming: '', witness: '', status: 'Pending',
  adminKeys: false, finRecords: false, minuteBooks: false, legalDocs: false
};

const refId = (label) => String(label).split(' - ')[0].trim();

/** The three sibling resources behind this screen. */
type CommitteeTab = 'terms' | 'members' | 'handover';
/** The Summary tab reads all three; the rest of the screen works on one. */
type CommitteeView = CommitteeTab | 'summary';

/**
 * What each tab puts on paper. The tabs store different columns, so each
 * prints its own register and its own single-record sheet; one shared layout
 * printed blank dates on every tab but the one it was written for.
 */
const TAB_INFO: Record<CommitteeTab, { title: string; register: string; record: string; recordType: string }> = {
  members: { title: 'Committee Members', register: 'COMMITTEE_MEMBER_REGISTER', record: 'COMMITTEE_MEMBER_RECORD', recordType: 'Committee Member' },
  terms: { title: 'Electoral Terms', register: 'COMMITTEE_REGISTER', record: 'COMMITTEE_TERM_RECORD', recordType: 'Electoral Term' },
  handover: { title: 'Secretary Handovers', register: 'SECRETARY_HANDOVER_REGISTER', record: 'SECRETARY_HANDOVER_RECORD', recordType: 'Secretary Handover' }
};

const CommitteeHistory = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [activeView, setActiveView] = useState<CommitteeView>('summary');
  const isSummary = activeView === 'summary';
  const activeTab: CommitteeTab = isSummary ? 'members' : activeView;
  const [viewMode, setViewMode] = useState('history');
  const [termForm, setTermForm] = useState(EMPTY_TERM);
  const [memberForm, setMemberForm] = useState(EMPTY_MEMBER);
  const [handoverForm, setHandoverForm] = useState(EMPTY_HANDOVER);
  const [editingId, setEditingId] = useState(null);
  // The row opened in the View dialog: every field it holds, and a print of it.
  const [viewing, setViewing] = useState(null);

  const isEditing = editingId !== null;
  const isDeletedView = viewMode === 'deleted';
  const { notification, notify, dismiss } = useNotification();

  const ledger = useMultiLedger({
    terms: { resource: termsApi, label: 'Term', prefix: 'TRM', statusField: 'termStatus' },
    members: { resource: membersApi, label: 'Committee Member', prefix: 'CMM', statusField: 'memberStatus' },
    handover: { resource: handoversApi, label: 'Secretary Handover', prefix: 'SHV', statusField: 'handoverStatus' }
  }, { notify });

  const roles = useOptionList('church_cm_roles');

  /**
   * Voiding a Term voids the committee members elected under it — otherwise
   * they stay active against a term that no longer exists. Runs as the
   * confirmVoid hook, before the refetch. Members carry their live/void flag
   * in `memberStatus`, so both that and `status` are stamped.
   */
  const cascadeVoidMembers = useCallback(async (voidedRow) => {
    if (voidedRow.__key !== 'terms') return;

    const children = ledger.active('members').filter((m) => m.termId === voidedRow.recordId);
    if (children.length === 0) return;

    const reason = `Cascade void from Term ${voidedRow.recordId}`;
    const results = await Promise.allSettled(children.map((member) =>
      membersApi.update(member.rawId, {
        ...member,
        id: member.rawId,
        status: 'VOID',
        memberStatus: 'VOID',
        deleteReason: reason,
        deletedAt: voidedRow.deletedAt || new Date().toLocaleDateString('en-IN', {
          day: '2-digit', month: 'short', year: 'numeric'
        })
      })));

    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      notify(`${failed} of ${children.length} linked members could not be voided.`, 'error');
    } else {
      notify(`${children.length} linked committee member(s) voided with the term.`, 'success');
    }
  }, [ledger, notify]);

  const terms = ledger.active('terms');
  const termById = useMemo(
    () => terms.reduce((acc, x) => ({ ...acc, [x.recordId]: x }), {}),
    [terms]
  );
  const termLabels = useMemo(() => terms.map((x) => `${x.recordId} - ${x.name}`), [terms]);

  const dataset = isDeletedView ? ledger.voided(activeTab) : ledger.active(activeTab);

  const getRowDate = useCallback((r) => r.date || r.fromDate || r.startDate, []);
  const range = useDateRangeFilter(dataset, getRowDate);
  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(
    range.filtered, ['recordId', 'name', 'role', 'outgoing', 'incoming', 'witness']
  );
  const page = usePagination(filtered);


  const resetForms = () => {
    setTermForm(EMPTY_TERM);
    setMemberForm(EMPTY_MEMBER);
    setHandoverForm(EMPTY_HANDOVER);
    setEditingId(null);
  };

  const switchTab = (tab) => {
    setActiveView(tab);
    setViewMode('history');
    resetForms();
    page.reset();
    clear();
    range.clear();
  };

  const switchView = (mode) => {
    if (mode !== 'add') resetForms();
    setViewMode(mode);
    page.reset();
  };

  // "Open the record" from the Approvals inbox: its tab (Deleted when it is
  // gone), then its full record.
  useOpenRecordFromLink(({ id, ref }) => {
    const row = findLedgerRow(ledger.rows, id, ref);
    if (!row) { notify(RECORD_GONE, 'error'); return; }
    const key = row.__key as CommitteeTab;
    switchTab(key);
    if ((ledger.voided(key) as unknown[]).includes(row)) setViewMode('deleted');
    setViewing(row);
  }, !ledger.loading);

  const startEdit = (item) => {
    if (activeTab === 'terms') {
      setTermForm({
        name: item.name || '', startDate: item.startDate || '', endDate: item.endDate || '',
        status: item.termStatus || 'Active', remarks: item.remarks || ''
      });
    } else if (activeTab === 'members') {
      const linked = termById[item.termId];
      setMemberForm({
        termRef: linked ? `${linked.recordId} - ${linked.name}` : item.termId || '',
        name: item.name || '', role: item.role || '',
        fromDate: item.fromDate || '', toDate: item.toDate || '',
        status: item.memberStatus || 'Active', remarks: item.remarks || ''
      });
    } else {
      setHandoverForm({ ...EMPTY_HANDOVER, ...item, status: item.handoverStatus || 'Pending' });
    }
    setEditingId(item.rawId);
    setViewMode('add');
  };

  const existingRecordId = (key) =>
    isEditing
      ? (ledger.active(key).find((r) => r.rawId === editingId) as { recordId?: string } | undefined)?.recordId
      : null;

  const handleTermSubmit = async (e) => {
    e.preventDefault();

    // Only one term may be Active — promoting a new one retires the old, saved
    // together (or sent for approval together).
    const retire: BatchOperation[] = [];
    if (!isEditing && termForm.status === 'Active') {
      const current = terms.find((x) => (x as { termStatus?: string }).termStatus === 'Active');
      if (current) {
        const { __key: _k, __raw: _r, recordType: _t, rawId, ...fields } = current as typeof current & { __raw?: unknown };
        retire.push(batch.change(termsApi, rawId, { ...fields, id: rawId, termStatus: 'Completed' }));
      }
    }

    const payload = {
      ...termForm,
      termStatus: termForm.status,
      recordId: existingRecordId('terms') || ledger.nextSequence('terms'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('terms', payload, editingId,
      { operations: retire, summary: 'New active committee term; the current one is completed' })) {
      resetForms(); setViewMode('history');
    }
  };

  const handleMemberSubmit = async (e) => {
    e.preventDefault();
    const role = memberForm.role.trim();
    const termId = refId(memberForm.termRef);
    // Both picked from search lists the browser cannot check — reported together (CHM-054).
    const missing = missingRequired([[role, 'Role'], [termId, 'Term']]);
    if (missing.length) { notify(requiredError(missing), 'error'); return; }

    roles.ensure(role);

    const { termRef, ...rest } = memberForm;
    const payload = {
      ...rest,
      role,
      termId,
      memberStatus: memberForm.status,
      recordId: existingRecordId('members') || ledger.nextSequence('members'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('members', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const handleHandoverSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...handoverForm,
      handoverStatus: handoverForm.status,
      recordId: existingRecordId('handover') || ledger.nextSequence('handover'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('handover', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const sub = { fontSize: '11px', color: t.textFaint };

  const termNameOf = (termId) => termById[termId]?.name || termId || 'Unknown Term';
  const yesNo = (v) => (v ? 'Handed over' : 'Not handed over');

  /**
   * A row as it prints: formatted dates, the term by name, the handover
   * checklist in words, and Created By. A member's period also fills
   * startDate / endDate so a layout written with those names still prints it.
   */
  const printRowOf = (r) => {
    const base = { ...r, createdBy: r.createdBy || '-', remarks: r.remarks || '-' };
    if (activeTab === 'members') {
      const fromDate = formatDate(r.fromDate);
      const toDate = r.toDate ? formatDate(r.toDate) : 'Present';
      return {
        ...base, fromDate, toDate, startDate: fromDate, endDate: toDate,
        period: `${fromDate} to ${toDate}`,
        termName: termNameOf(r.termId),
        memberStatus: r.memberStatus || '-'
      };
    }
    if (activeTab === 'terms') {
      return {
        ...base,
        startDate: formatDate(r.startDate),
        endDate: formatDate(r.endDate),
        termStatus: r.termStatus || '-',
        memberCount: ledger.active('members').filter((m) => m.termId === r.recordId).length
      };
    }
    const handed = CHECKLIST.filter((c) => r[c.key]).map((c) => c.label);
    return {
      ...base,
      date: formatDate(r.date),
      witness: r.witness || '-',
      handoverStatus: r.handoverStatus || '-',
      checklist: handed.length ? handed.join(', ') : 'None recorded',
      ...Object.fromEntries(CHECKLIST.map((c) => [`${c.key}Label`, yesNo(r[c.key])]))
    };
  };

  /** The View dialog's lines: every field this tab records. */
  const viewFieldsOf = (r): Array<[string, string]> => {
    const p = printRowOf(r);
    const lines: Array<[string, unknown]> = activeTab === 'members'
      ? [
          ['Record ID', p.recordId], ['Name', p.name], ['Role', p.role],
          ['Term', `${p.termName}${r.termId ? ` (${r.termId})` : ''}`],
          ['From Date', p.fromDate], ['To Date', p.toDate],
          ['Member Status', p.memberStatus], ['Remarks / Portfolio Notes', p.remarks]
        ]
      : activeTab === 'terms'
        ? [
            ['Record ID', p.recordId], ['Term Name', p.name],
            ['Start Date', p.startDate], ['End Date', p.endDate],
            ['Term Status', p.termStatus], ['Members Elected', p.memberCount],
            ['Remarks', p.remarks]
          ]
        : [
            ['Record ID', p.recordId], ['Handover Date', p.date],
            ['Outgoing Secretary', p.outgoing], ['Incoming Secretary', p.incoming],
            ['Witness', p.witness], ['Handover Status', p.handoverStatus],
            ...CHECKLIST.map((c) => [c.label, p[`${c.key}Label`]] as [string, unknown])
          ];
    return [
      ...lines,
      ['Created By', p.createdBy],
      ...(r.deletedAt ? [['Deleted On', r.deletedAt] as [string, unknown]] : []),
      ...(r.deleteReason ? [['Delete Reason', r.deleteReason] as [string, unknown]] : [])
    ].map(([label, value]) => [label, value === undefined || value === null || value === '' ? '-' : String(value)]);
  };

  const viewButton = (r) => (
    <Button variant="ghost" size="sm" title="View full record" onClick={() => setViewing(r)}>👁️ View</Button>
  );

  const serialColumn = {
    key: 'serial',
    header: 'S.No',
    nowrap: true,
    render: (r, idx) => (
      <>
        <strong>{(page.currentPage - 1) * page.itemsPerPage + idx + 1}</strong>
        <br /><span style={sub}>{r.recordId}</span>
      </>
    )
  };


  const columnsByTab = {
    members: [
      serialColumn,
      {
        key: 'name',
        header: 'Member & Role',
        render: (r) => (<><strong>{r.name}</strong><br /><span style={{ fontSize: '12px', color: t.textFaint }}>{r.role}</span></>)
      },
      { key: 'term', header: 'Term', render: (r) => <strong>{termById[r.termId]?.name || r.termId || 'Unknown Term'}</strong> },
      {
        key: 'period',
        header: 'Period',
        nowrap: true,
        render: (r) => `${formatDate(r.fromDate)} to ${r.toDate ? formatDate(r.toDate) : 'Present'}`
      },
      {
        key: 'status',
        header: 'Status',
        // Remarks / portfolio notes can run long; they are read in View.
        render: (r) => <Badge tone={r.memberStatus === 'Active' ? 'success' : 'neutral'}>{r.memberStatus || '-'}</Badge>
      }
    ],
    terms: [
      serialColumn,
      { key: 'name', header: 'Term Name', render: (r) => <strong>{r.name}</strong> },
      { key: 'startDate', header: 'Start Date', nowrap: true, render: (r) => formatDate(r.startDate) },
      { key: 'endDate', header: 'End Date', nowrap: true, render: (r) => formatDate(r.endDate) },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <Badge tone={r.termStatus === 'Active' ? 'success' : 'neutral'}>{r.termStatus || '-'}</Badge>
      }
    ],
    handover: [
      serialColumn,
      { key: 'date', header: 'Date', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
      { key: 'outgoing', header: 'Outgoing', render: (r) => <span style={{ color: t.danger, fontWeight: 'bold' }}>{r.outgoing}</span> },
      { key: 'incoming', header: 'Incoming', render: (r) => <span style={{ color: t.income, fontWeight: 'bold' }}>{r.incoming}</span> },
      {
        key: 'status',
        header: 'Status',
        render: (r) => (
          <>
            <Badge tone={r.handoverStatus === 'Completed' ? 'success' : 'warning'}>{r.handoverStatus || '-'}</Badge>
            <div style={{ ...sub, marginTop: '4px' }}>Witness: {r.witness || '-'}</div>
          </>
        )
      }
    ]
  };

  const tailColumns = isDeletedView
    ? [
        { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={sub}>{r.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
        { key: 'actions', header: 'Actions', align: 'right', render: viewButton }
      ]
    : [
        {
          key: 'actions',
          header: 'Actions',
          align: 'right',
          render: (r) => (
            <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
              {viewButton(r)}
              <Button write variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
              <Button variant="danger" size="sm" style={styles.deleteBtn} onClick={() => ledger.requestVoid(r)}>Delete</Button>
            </div>
          )
        }
      ];

  const columns = [
    ...columnsByTab[activeTab],
    { key: 'createdBy', header: 'Created By', render: (r) => (r as { createdBy?: string }).createdBy || '-' },
    ...tailColumns
  ];

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      {/* Ticking a handover row reveals its printable document, which carries
          its own template and print scope. */}

      <div>
        <PageHeader
          icon="🏛️"
          title="Committee History"
          activeTab={activeView}
          onTabChange={switchTab}
          tabs={[
            { key: 'summary', label: '📊 Summary' },
            { key: 'members', label: '👥 Committee Members' },
            { key: 'terms', label: '📅 Electoral Terms' },
            { key: 'handover', label: '🤝 Secretary Handover' }
          ]}
        />

        {isSummary && (
          <CommitteeSummary
            terms={terms}
            members={ledger.active('members')}
            handovers={ledger.active('handover')}
            loading={ledger.loading}
            isDarkMode={isDarkMode}
          />
        )}

        {!isSummary && (
        <SubTabs
          value={viewMode}
          onChange={switchView}
          tabs={[
            { key: 'history', label: '📋 View Active Records' },
            { key: 'deleted', label: '🗑️ Deleted', variant: 'danger' },
            { key: 'add', write: true, label: isEditing ? '✎ Editing Record...' : '+ Add New Entry' }
          ]}
        />
        )}

        {!isSummary && (viewMode === 'history' || isDeletedView) && (
          <>
            <SearchBar
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); page.reset(); }}
              onClear={() => { clear(); page.reset(); }}
              placeholder="Search records..."
              actions={
                <>
                  <DateRangeFilter {...range} onChange={page.reset} />
                  <PrintButton
                    module="Committee History"
                    documentType={TAB_INFO[activeTab].register}
                    size="sm"
                    title={`Committee-${TAB_INFO[activeTab].title.replace(/ /g, '-')}`}
                    data={() => ({
                      title: `Committee Register — ${TAB_INFO[activeTab].title}${isDeletedView ? ' (Deleted)' : ''}`,
                      reference: [
                        searchTerm && `Search: "${searchTerm}"`,
                        `Printed ${formatDate(new Date().toISOString())}`
                      ].filter(Boolean).join(' · '),
                      // Every line that matches the filters, not just the page on screen.
                      rowCount: filtered.length,
                      rows: filtered.map(printRowOf)
                    })}
                    onNotify={notify}
                    isDarkMode={isDarkMode}
                  />
                </>
              }
            />

            {/* Stood down while a handover preview is open — that owns the page. */}
            <TablePanel
              title={<>Committee History — {activeTab === 'members' ? 'Committee Members' : activeTab === 'terms' ? 'Electoral Terms' : 'Secretary Handover'}</>}
              below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />}
                columns={columns}
                rows={page.currentItems}
                rowKey="rawId"
                loading={ledger.loading}
                emptyMessage="No records found for this category."
            />
          </>
        )}

        {!isSummary && viewMode === 'add' && activeTab === 'members' && (
          <FormLayout
            title={isEditing ? 'Edit Committee Member' : 'Register Committee Member'}
            submitLabel={isEditing ? 'Update Member' : 'Register Member'}
            submitting={ledger.saving}
            onSubmit={handleMemberSubmit}
            onCancel={isEditing ? () => switchView('history') : undefined}
            cancelLabel="Cancel Edit"
          >
            <FormLayout.Grid>
              <SearchableSelect
                label="Select Term / Period" required fullWidth
                allowAdd={false} allowManage={false}
                placeholder="Search terms (e.g. 2024-2027)..."
                value={memberForm.termRef}
                onChange={(v) => setMemberForm((prev) => ({ ...prev, termRef: v }))}
                options={termLabels}
              />
              <Input
                label="Member Name" required placeholder="e.g. John Samuel"
                value={memberForm.name}
                onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                style={{ fontWeight: 'bold' }}
              />
              <SearchableSelect
                label="Committee Role" required placeholder="Search role..."
                value={memberForm.role}
                onChange={(v) => setMemberForm((prev) => ({ ...prev, role: v }))}
                options={roles.options} onOptionsChange={roles.setOptions}
              />
              <Input
                label="From Date (Elected/Co-opted)" type="date" required
                value={memberForm.fromDate}
                onChange={(e) => setMemberForm({ ...memberForm, fromDate: e.target.value })}
              />
              <Input
                label="To Date (Relieved/Resigned)" type="date"
                value={memberForm.toDate}
                onChange={(e) => setMemberForm({ ...memberForm, toDate: e.target.value })}
              />
              <Select
                label="Status" placeholder=""
                options={MEMBER_STATUSES}
                value={memberForm.status}
                onChange={(e) => setMemberForm({ ...memberForm, status: e.target.value })}
              />
              <Input
                label="Remarks / Portfolio Notes"
                placeholder="e.g. Also represents Youth Fellowship..."
                value={memberForm.remarks}
                onChange={(e) => setMemberForm({ ...memberForm, remarks: e.target.value })}
              />
            </FormLayout.Grid>
          </FormLayout>
        )}

        {!isSummary && viewMode === 'add' && activeTab === 'terms' && (
          <FormLayout
            title={isEditing ? 'Edit Electoral Term' : 'Define Electoral Term'}
            submitLabel={isEditing ? 'Update Term' : 'Save Term'}
            submitting={ledger.saving}
            onSubmit={handleTermSubmit}
            onCancel={isEditing ? () => switchView('history') : undefined}
            cancelLabel="Cancel Edit"
          >
            <FormLayout.Grid>
              <Input
                label="Term Name / Designation" required fullWidth placeholder="e.g. Term 2024-2027"
                value={termForm.name}
                onChange={(e) => setTermForm({ ...termForm, name: e.target.value })}
                style={{ fontSize: '16px', fontWeight: 'bold' }}
              />
              <Input
                label="Start Date" type="date" required
                value={termForm.startDate}
                onChange={(e) => setTermForm({ ...termForm, startDate: e.target.value })}
              />
              <Input
                label="End Date" type="date" required
                value={termForm.endDate}
                onChange={(e) => setTermForm({ ...termForm, endDate: e.target.value })}
              />
              <Select
                label="Status" placeholder=""
                options={TERM_STATUSES}
                value={termForm.status}
                onChange={(e) => setTermForm({ ...termForm, status: e.target.value })}
                hint="Saving a new Active term marks the previous one Completed."
              />
              <Input
                label="Remarks / Mandate" placeholder="e.g. Focus on church expansion..."
                value={termForm.remarks}
                onChange={(e) => setTermForm({ ...termForm, remarks: e.target.value })}
              />
            </FormLayout.Grid>
          </FormLayout>
        )}

        {!isSummary && viewMode === 'add' && activeTab === 'handover' && (
          <FormLayout
            title={isEditing ? 'Edit Handover Log' : 'Execute Handover / Takeover Checklist'}
            submitLabel={isEditing ? 'Update Handover Log' : 'Save Handover'}
            submitting={ledger.saving}
            onSubmit={handleHandoverSubmit}
            onCancel={isEditing ? () => switchView('history') : undefined}
            cancelLabel="Cancel Edit"
          >
            <FormLayout.Grid>
              <Input
                label="Handover Date" type="date" required fullWidth
                value={handoverForm.date}
                onChange={(e) => setHandoverForm({ ...handoverForm, date: e.target.value })}
              />
              <Input
                label="Outgoing Secretary" required placeholder="Leaving Secretary"
                value={handoverForm.outgoing}
                onChange={(e) => setHandoverForm({ ...handoverForm, outgoing: e.target.value })}
                style={{ color: t.danger, fontWeight: 'bold' }}
              />
              <Input
                label="Incoming Secretary" required placeholder="New Secretary"
                value={handoverForm.incoming}
                onChange={(e) => setHandoverForm({ ...handoverForm, incoming: e.target.value })}
                style={{ color: t.income, fontWeight: 'bold' }}
              />
              <Input
                label="Witness (Pastor / Presbyter)" fullWidth placeholder="Name of Witness"
                value={handoverForm.witness}
                onChange={(e) => setHandoverForm({ ...handoverForm, witness: e.target.value })}
              />

              <div style={{ gridColumn: '1 / -1', marginTop: '15px' }}>
                <h4 style={{ margin: '0 0 15px 0', borderBottom: `1px solid ${t.border}`, paddingBottom: '5px', color: t.textMain }}>
                  Handover Checklist Verifications
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {CHECKLIST.map((item) => (
                    <label
                      key={item.key}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        fontSize: t.fontSize.md, cursor: 'pointer', color: t.textMain,
                        backgroundColor: t.bgSubtle, padding: '10px', borderRadius: t.radius.md
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(handoverForm[item.key])}
                        onChange={(e) => setHandoverForm({ ...handoverForm, [item.key]: e.target.checked })}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>

              <Select
                label="Final Handover Status" placeholder="" fullWidth
                options={HANDOVER_STATUSES}
                value={handoverForm.status}
                onChange={(e) => setHandoverForm({ ...handoverForm, status: e.target.value })}
              />
            </FormLayout.Grid>
          </FormLayout>
        )}
      </div>

      {viewing && (
        <Modal
          open
          title={`${TAB_INFO[activeTab].recordType} — ${viewing.recordId || ''}`}
          onClose={() => setViewing(null)}
          isDarkMode={isDarkMode}
          width="600px"
          footer={
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setViewing(null)}>Close</Button>
              <PrintButton
                module="Committee History"
                documentType={TAB_INFO[activeTab].record}
                variant="primary"
                title={`Committee-${viewing.recordId || viewing.rawId}`}
                data={() => ({ ...printRowOf(viewing), title: TAB_INFO[activeTab].recordType })}
                onNotify={notify}
                isDarkMode={isDarkMode}
              />
            </div>
          }
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: t.fontSize.md }}>
            <tbody>
              {viewFieldsOf(viewing).map(([label, value]) => (
                // Colours come from the theme: the modal panel sets only a
                // background, so unstyled text would stay dark in dark mode.
                <tr key={label} style={{ borderBottom: `1px solid ${t.border}` }}>
                  <td style={{ padding: '7px 10px', color: t.textMuted, width: '200px', verticalAlign: 'top' }}>{label}</td>
                  <td style={{
                    padding: '7px 10px', fontWeight: 'bold', verticalAlign: 'top',
                    color: label === 'Delete Reason' || value === 'Not handed over' ? t.danger
                      : value === 'Handed over' ? t.income : t.textMain,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                  }}>
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}

      <PromptDialog
        open={Boolean(ledger.voiding)}
        title={`Delete ${ledger.voiding?.recordType || ''} ${ledger.voiding?.recordId || ''}`}
        message="Please enter the reason for voiding this record. It will be kept in Deleted History."
        placeholder="Reason for voiding"
        confirmLabel="Delete"
        requiredError="A reason is required to remove a record."
        onSubmit={(reason) => ledger.confirmVoid(reason, cascadeVoidMembers)}
        onCancel={ledger.cancelVoid}
      />
    </div>
  );
};

export default CommitteeHistory;
